import { useTheme } from '@mui/material';
import type { TouchRippleActions } from '@mui/material/ButtonBase/TouchRipple';
import { animate, PanInfo, useMotionValue, useTransform } from 'framer-motion';
import { type MouseEvent, useCallback, useRef, useState } from 'react';
import { ColoredAvatar, DynamicIcon, EllipsesMenu } from 'src/components';
import type { EllipsesMenuOption } from 'src/components/EllipsesMenu/ellipses-menu';
import { Notification, NotificationStatus } from 'src/graphql/entities/notification';
import {
  getNotificationConversationFriendlyId,
  getNotificationEntityName,
} from 'src/graphql/entities/notification/notification-target';
import { EntityNames } from 'src/graphql/types';
import { formatLastMessageSinceTime } from 'src/utils/date';
import { getThemeColor } from 'src/utils/strings';
import { useScreenDimensions } from 'src/utils/system';
import { LongPressEventType, useLongPress } from 'use-long-press';
import { useSuppressSelection } from './hooks/use-suppress-selection';
import {
  INBOX_PANE_ITEM_ACTIONS_TEST_ID,
  INBOX_PANE_ITEM_META_TEST_ID,
  INBOX_PANE_ITEM_TEST_ID,
  INBOX_PANE_ITEM_UNREAD_DOT_TEST_ID,
  ROW_MENU_LONG_PRESS_MOVE_TOLERANCE_PX,
  ROW_MENU_LONG_PRESS_MS,
  SWIPE_ARCHIVE_THRESHOLD_PX,
} from './inbox-pane.constants';
import {
  ArchiveRevealBackground,
  AvatarCell,
  ConversationId,
  ItemRow,
  Meta,
  MotionSwipeable,
  RightCol,
  ROW_MENU_CLASS,
  ROW_META_CLASS,
  ROW_TEXT_CLASS,
  RowMenuSlot,
  RowMeta,
  Snippet,
  SubjectLine,
  SubjectText,
  SwipeContainer,
  UnreadDot,
} from './inbox-pane.styles';

// entityStatus.colorPath comes from statusDisplayPropsMap (primary/secondary/
// tertiary "system" roles); the mockup wants those grouped into its own
// open/waiting/done glyph palette instead of the raw system colors.
const STATUS_COLOR_PATH_TO_GLYPH_COLOR: Record<string, string> = {
  'custom.OV.sys.primary': 'custom.OV.sys.primary',
  'custom.OV.sys.secondary': 'custom.OV.extended.Signature.color',
  'custom.OV.sys.tertiary': 'custom.OV.sys.success',
};

export const InboxPaneItem = ({
  notification,
  isSelected,
  isActive,
  menuItems,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onSwipeArchive,
}: {
  notification: Notification;
  isSelected: boolean;
  isActive: boolean;
  menuItems?: EllipsesMenuOption[];
  onClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onSwipeArchive?: () => void;
}) => {
  const theme = useTheme();
  const { isTouchDevice } = useScreenDimensions();
  const rowRef = useRef<HTMLButtonElement | null>(null);
  const rippleRef = useRef<TouchRippleActions | null>(null);

  // ButtonBase clears its ripple only on a touch-end over the row; a long press ends on
  // the menu backdrop and a swipe ends mid-drag, so both would leave it stuck on screen.
  const stopRipple = useCallback(() => rippleRef.current?.stop(), []);

  const hasMenu = !!menuItems?.length;
  const enableSwipe = isTouchDevice && !!onSwipeArchive;

  const [isSwiping, setIsSwiping] = useState(false);
  const [isLongPressMenuOpen, setIsLongPressMenuOpen] = useState(false);
  const [isRowMenuOpen, setIsRowMenuOpen] = useState(false);

  useSuppressSelection(isLongPressMenuOpen);

  const x = useMotionValue(0);
  const revealOpacity = useTransform(x, [-SWIPE_ARCHIVE_THRESHOLD_PX, 0], [1, 0], {
    clamp: true,
  });

  const handleDragEnd = (_: PointerEvent, info: PanInfo) => {
    setIsSwiping(false);
    if (info.offset.x <= -SWIPE_ARCHIVE_THRESHOLD_PX) {
      // archive only after the fly-out, so the list's exit animation composes from there
      animate(x, -window.innerWidth, { duration: 0.2 });
      requestAnimationFrame(() => onSwipeArchive?.());
    } else {
      animate(x, 0, { type: 'spring', stiffness: 500, damping: 40 });
    }
  };

  const handleRowClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      // a swipe must not also register as a row click
      if (isSwiping) {
        return;
      }

      // React bubbles clicks from the row's portalled dropdown up to here — including the
      // backdrop click that closes it, which lands anywhere on screen
      if (!rowRef.current?.contains(event.target as Node)) {
        return;
      }

      onClick();
    },
    [isSwiping, onClick],
  );

  const longPressHandlers = useLongPress(
    hasMenu && isTouchDevice
      ? () => {
          stopRipple();
          setIsLongPressMenuOpen(true);
        }
      : null,
    {
      threshold: ROW_MENU_LONG_PRESS_MS,
      detect: LongPressEventType.Touch,
      cancelOnMovement: ROW_MENU_LONG_PRESS_MOVE_TOLERANCE_PX,
    },
  );

  const isUnread = notification.status === NotificationStatus.Unread;
  const friendlyId =
    getNotificationEntityName(notification.entity) === EntityNames.Conversation
      ? getNotificationConversationFriendlyId(notification)
      : null;
  const glyphColorPath =
    STATUS_COLOR_PATH_TO_GLYPH_COLOR[notification.entityStatus.colorPath] ??
    notification.entityStatus.colorPath;

  return (
    <SwipeContainer>
      {!!enableSwipe && (
        <ArchiveRevealBackground style={{ opacity: revealOpacity }}>
          <DynamicIcon name="archive" />
        </ArchiveRevealBackground>
      )}

      <MotionSwipeable
        drag={enableSwipe ? 'x' : false}
        // lock to the dominant axis first, so scrolling the list can't drag rows sideways
        dragDirectionLock
        dragConstraints={{ left: -window.innerWidth, right: 0 }}
        dragElastic={{ left: 0.6, right: 0 }}
        dragMomentum={false}
        style={{ x }}
        onDragStart={() => {
          stopRipple();
          setIsSwiping(true);
        }}
        onDragEnd={handleDragEnd}
      >
        <ItemRow
          ref={rowRef}
          touchRippleRef={rippleRef}
          isSelected={isSelected}
          isActive={isActive}
          isRead={!isUnread}
          onClick={handleRowClick}
          onMouseEnter={onMouseEnter}
          // the open menu's backdrop takes the pointer off the row; stay the shortcut target
          onMouseLeave={() => !isRowMenuOpen && onMouseLeave()}
          data-testid={INBOX_PANE_ITEM_TEST_ID}
          {...longPressHandlers()}
        >
          <AvatarCell>
            <ColoredAvatar name={notification.author.fullName} />
          </AvatarCell>

          <Meta className={ROW_TEXT_CLASS}>
            <SubjectLine isUnread={isUnread}>
              {!!isUnread && <UnreadDot data-testid={INBOX_PANE_ITEM_UNREAD_DOT_TEST_ID} />}
              {friendlyId ? <ConversationId>#{friendlyId}</ConversationId> : null}
              <SubjectText>{notification.subject}</SubjectText>
            </SubjectLine>
            <Snippet>{notification.title}</Snippet>
          </Meta>

          <RightCol>
            <RowMeta
              isMenuOpen={isRowMenuOpen}
              className={ROW_META_CLASS}
              data-testid={INBOX_PANE_ITEM_META_TEST_ID}
            >
              <DynamicIcon
                name={notification.entityStatus.icon}
                size={16}
                sx={{ color: getThemeColor(glyphColorPath, theme) }}
              />
              <span>{formatLastMessageSinceTime(notification.createdAt)}</span>
            </RowMeta>

            {!!hasMenu && !isTouchDevice && (
              <RowMenuSlot
                isMenuOpen={isRowMenuOpen}
                className={ROW_MENU_CLASS}
                data-testid={INBOX_PANE_ITEM_ACTIONS_TEST_ID}
              >
                <EllipsesMenu
                  options={menuItems}
                  triggerTestId="inbox-pane-item-menu"
                  onOpenChange={setIsRowMenuOpen}
                  sx={{ width: '1.5rem', height: '1.5rem' }}
                />
              </RowMenuSlot>
            )}
          </RightCol>
        </ItemRow>
      </MotionSwipeable>

      {!!hasMenu && !!isTouchDevice && (
        <EllipsesMenu
          options={menuItems}
          hideTrigger
          open={isLongPressMenuOpen}
          anchorEl={rowRef.current}
          onClose={() => setIsLongPressMenuOpen(false)}
        />
      )}
    </SwipeContainer>
  );
};
